/**
 * The Coast Guard IAP cover sheet, as an editable card.
 *
 * The cover existed only as a generated PDF before this: what the plan carried could be chosen on screen, but who
 * approved it could not, so the approval box was filled from whoever happened to hold the seat at export time. Seating
 * the real form here makes box 4 a signature rather than a printed name, and keeps boxes 1 to 3 visible so the sheet
 * reads the way it prints.
 *
 * Boxes 1 and 2 are derived from the incident. Box 3 prints the same window and opens the incident workspace’s next-period schedule.
 */

import React from 'react';

import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useOperationalPeriodWindow } from 'components/IAP/hooks/useOperationalPeriodWindow';
import { useGeneratedIapForPeriod } from 'components/IAP/hooks/useGeneratedIapForPeriod';
import FormVersioningPanel from 'components/IAP/femaCards/FormVersioningPanel';
import { selectGroup, useAppSelector } from 'slices/commonSelectors';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import { OperationalPeriodScheduleTrigger } from '../OperationalPeriodScheduleControl';
import { IcsPreparedByBox } from '../IcsPreparedByBox';
import IapCoverApprovalTable from './IapCoverApprovalTable';
import IapCoverAttachmentsBox from './IapCoverAttachmentsBox';
import { IAP_COVER_BASE_ID } from './iapCoverForm';
import {
  IAP_COVER_APPROVAL_HINT,
  IAP_COVER_ATTACHMENTS_HINT,
  IAP_COVER_BOX_LABELS,
  IAP_COVER_OPERATIONAL_PERIOD_HINT,
} from './iapCoverBoxLabels';

type IapCoverSheetCardProps = {
  /**
   * The period the cover is being prepared for, which is the one the bundle will carry. Taken from the IAP widget
   * rather than the planning context so the version being signed and the generated `IAP-OPn` file it gates on are
   * always the same period.
   */
  periodNumber: number;
  readOnly?: boolean;
};

const IapCoverSheetCard: React.FC<IapCoverSheetCardProps> = ({
  periodNumber,
  readOnly = false,
}) => {
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const orgGroup = useAppSelector(selectGroup);
  // Unpinned: the cover prints the scheduled next-period window, which is what
  // the working package resolves to and what the rest of the plan prints.
  const { opFrom, opTo } = useOperationalPeriodWindow();
  const generatedIap = useGeneratedIapForPeriod(periodNumber);

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3"
      data-testid="iap-cover-sheet-card"
      data-tour="iap-cover"
    >
      <FormVersioningPanel
        baseId={IAP_COVER_BASE_ID}
        periodNumber={periodNumber}
        readOnly={readOnly}
        signDisabledReason={
          generatedIap ? null : 'generate the IAP first'
        }
      />

      <div className="tw:grid tw:min-w-0 tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={IAP_COVER_BOX_LABELS.incidentName}
          testId="iap-cover-section-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="iap-cover-field-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={IAP_COVER_BOX_LABELS.incidentLocation}
          testId="iap-cover-section-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="iap-cover-field-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={IAP_COVER_BOX_LABELS.operationalPeriod}
          testId="iap-cover-section-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label="About the operational period">
              {IAP_COVER_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleTrigger testId="iap-cover-operational-period">
            <div className="tw:grid tw:gap-1 tw:text-sm">
              <div>
                <span className="tw:text-muted-foreground">From: </span>
                <span data-testid="iap-cover-field-op-from">{opFrom}</span>
              </div>
              <div>
                <span className="tw:text-muted-foreground">To: </span>
                <span data-testid="iap-cover-field-op-to">{opTo}</span>
              </div>
            </div>
          </OperationalPeriodScheduleTrigger>
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={IAP_COVER_BOX_LABELS.approvedByIc}
        testId="iap-cover-section-approved-by"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About Incident Commander approval">
            {IAP_COVER_APPROVAL_HINT}
          </BoxInfo>
        }
      >
        <IapCoverApprovalTable
          periodNumber={periodNumber}
          organizationName={orgGroup?.group_name ?? ''}
          iapGenerated={generatedIap != null}
          readOnly={readOnly}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={IAP_COVER_BOX_LABELS.formsAndDocuments}
        testId="iap-cover-section-forms-and-documents"
        className="tw:mt-0"
        headerAction={
          <BoxInfo label="About the forms and documents">
            {IAP_COVER_ATTACHMENTS_HINT}
          </BoxInfo>
        }
      >
        <IapCoverAttachmentsBox disabled={readOnly} />
      </Ics204CardSection>

      <IcsPreparedByBox
        baseId={IAP_COVER_BASE_ID}
        periodNumber={periodNumber}
        boxLabel={IAP_COVER_BOX_LABELS.preparedBy}
        testId="iap-cover-section-prepared-by"
        unsignedMessage="Not signed. Generate the IAP, then create a signed version above to sign the cover."
      />
    </div>
  );
};

export default IapCoverSheetCard;
